import { z } from "zod";

/**
 * Flow lint (#352). A customer's flow content is configuration, not CX
 * content (ADR-0006), but it still never leaves their account: the
 * inventory sync reads each flow a phone number routes to, runs these
 * rules there, and sends only the findings - the rule, a short reason in
 * our own words, and the block identifier - never the flow's JSON or any
 * text a block holds.
 *
 * - `flow-logging-enabled`: an inbound flow a number reaches enables
 *   *Set logging behavior* before its first menu or transfer. Without it
 *   Connect writes no flow logs, and Journeys › Paths stays empty.
 * - `starsach-lander-wired`: a flow a bound number reaches calls the
 *   lander or a Starsach flow module. Otherwise the profile's hours,
 *   holidays and emergency close do nothing on that number.
 */
export const FlowLintRuleSchema = z.enum(["flow-logging-enabled", "starsach-lander-wired"]);
export type FlowLintRule = z.infer<typeof FlowLintRuleSchema>;

export const FlowFindingSchema = z.object({
  rule: FlowLintRuleSchema,
  severity: z.enum(["error", "warning"]),
  /** One sentence, written by the rule - never text taken from the flow. */
  reason: z.string().min(1).max(400),
  /** The block the finding is about, by its identifier in the flow, where there is one. */
  block: z.string().min(1).max(256).optional(),
});
export type FlowFinding = z.infer<typeof FlowFindingSchema>;

/**
 * `Environments.flow_lint`: which flows carry a finding, by name, as of the
 * last inventory push that listed flows. Absent until a template that lints
 * has pushed; the Customers strip and the Journeys empty states read it.
 */
export const FlowLintSummarySchema = z.object({
  checked_at: z.string().datetime({ offset: true }),
  unlogged_flows: z.array(z.string()).max(50),
  unwired_flows: z.array(z.string()).max(50),
});
export type FlowLintSummary = z.infer<typeof FlowLintSummarySchema>;

/**
 * The summary over an environment's flow items. `undefined` when no flow was
 * linted at all (a template before #352), so "not checked" never reads as clean.
 */
export function summarizeFlowLint(items: Array<{ type: string; name: string; findings?: FlowFinding[] | undefined }>, checkedAt: string): FlowLintSummary | undefined {
  const linted = items.filter((i) => i.type === "flow" && i.findings !== undefined);
  if (linted.length === 0) return undefined;
  const named = (rule: FlowLintRule) => linted.filter((i) => i.findings!.some((f) => f.rule === rule)).map((i) => i.name).sort().slice(0, 50);
  return { checked_at: checkedAt, unlogged_flows: named("flow-logging-enabled"), unwired_flows: named("starsach-lander-wired") };
}
